<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { Check, Copy, KeyRound, MonitorSmartphone, Plus, RefreshCw, RotateCw, Save, ShieldCheck, X } from "lucide-vue-next";
import { api } from "../services/api";
import type { Agent, ClientApp, ClientPlatform } from "../types";

type ClientDraft = {
  slug: string;
  name: string;
  platform: ClientPlatform;
  isEnabled: boolean;
  agentIds: string[];
};

const clients = ref<ClientApp[]>([]);
const agents = ref<Agent[]>([]);
const selectedId = ref("");
const draft = ref<ClientDraft>(emptyDraft());
const createDraft = ref<ClientDraft>(emptyDraft());
const loading = ref(false);
const saving = ref(false);
const rotating = ref(false);
const creating = ref(false);
const createOpen = ref(false);
const keyOpen = ref(false);
const revealedKey = ref("");
const revealedClientName = ref("");
const copied = ref(false);
const error = ref("");
const createError = ref("");
const success = ref("");

const selectedClient = computed(() => clients.value.find((client) => client.id === selectedId.value));

function emptyDraft(): ClientDraft {
  return { slug: "", name: "", platform: "OTHER", isEnabled: true, agentIds: [] };
}

function selectClient(client: ClientApp): void {
  selectedId.value = client.id;
  draft.value = {
    slug: client.slug,
    name: client.name,
    platform: client.platform,
    isEnabled: client.isEnabled,
    agentIds: [...client.agentIds],
  };
  error.value = "";
  success.value = "";
}

async function load(): Promise<void> {
  loading.value = true;
  error.value = "";
  try {
    const [nextClients, nextAgents] = await Promise.all([api.clientApps(), api.adminAgents()]);
    clients.value = nextClients;
    agents.value = nextAgents;
    const selected = nextClients.find((client) => client.id === selectedId.value) ?? nextClients[0];
    if (selected) selectClient(selected);
    else {
      selectedId.value = "";
      draft.value = emptyDraft();
    }
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : "客户端列表加载失败";
  } finally {
    loading.value = false;
  }
}

function openCreate(): void {
  createDraft.value = emptyDraft();
  createError.value = "";
  createOpen.value = true;
}

function showKey(clientName: string, key: string): void {
  revealedClientName.value = clientName;
  revealedKey.value = key;
  copied.value = false;
  keyOpen.value = true;
}

function closeKey(): void {
  keyOpen.value = false;
  revealedKey.value = "";
  revealedClientName.value = "";
  copied.value = false;
}

async function createClient(): Promise<void> {
  if (!createDraft.value.name.trim() || !createDraft.value.slug.trim()) return;
  creating.value = true;
  createError.value = "";
  try {
    const result = await api.createClientApp({
      name: createDraft.value.name.trim(),
      slug: createDraft.value.slug.trim(),
      platform: createDraft.value.platform,
      agentIds: createDraft.value.agentIds,
    });
    createOpen.value = false;
    selectedId.value = result.client.id;
    await load();
    showKey(result.client.name, result.clientKey);
  } catch (reason) {
    createError.value = reason instanceof Error ? reason.message : "客户端创建失败";
  } finally {
    creating.value = false;
  }
}

async function saveClient(): Promise<void> {
  if (!selectedClient.value || !draft.value.name.trim() || !draft.value.slug.trim()) return;
  saving.value = true;
  error.value = "";
  success.value = "";
  try {
    await api.updateClientApp(selectedClient.value.id, {
      name: draft.value.name.trim(),
      slug: draft.value.slug.trim(),
      platform: draft.value.platform,
      isEnabled: draft.value.isEnabled,
    });
    await api.setClientAgents(selectedClient.value.id, draft.value.agentIds);
    await load();
    success.value = draft.value.isEnabled ? "客户端配置已保存" : "客户端已停用，原 Key 无法继续访问";
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : "客户端配置保存失败";
  } finally {
    saving.value = false;
  }
}

async function rotateKey(): Promise<void> {
  if (!selectedClient.value || !window.confirm(`确定轮换“${selectedClient.value.name}”的客户端 Key？原 Key 将立即失效。`)) return;
  rotating.value = true;
  error.value = "";
  success.value = "";
  try {
    const result = await api.rotateClientKey(selectedClient.value.id);
    await load();
    showKey(result.client.name, result.clientKey);
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : "客户端 Key 轮换失败";
  } finally {
    rotating.value = false;
  }
}

async function copyKey(): Promise<void> {
  await navigator.clipboard.writeText(revealedKey.value);
  copied.value = true;
}

function formatTime(value?: string | null): string {
  return value ? new Date(value).toLocaleString("zh-CN") : "从未使用";
}

function platformLabel(platform: ClientPlatform): string {
  return { ANDROID: "Android", WINDOWS: "Windows", WEB: "Web", OTHER: "其他" }[platform];
}

onMounted(() => void load());
</script>

<template>
  <div class="client-layout">
    <aside class="surface-section client-list-panel">
      <div class="section-header">
        <div><span class="eyebrow">ACCESS CLIENTS</span><h2>客户端列表</h2></div>
        <div class="header-command-group">
          <button class="icon-button compact" type="button" title="刷新客户端" :disabled="loading" @click="load"><RefreshCw :size="16" :class="{ spinning: loading }" /></button>
          <button class="icon-button compact" type="button" title="创建客户端" @click="openCreate"><Plus :size="17" /></button>
        </div>
      </div>
      <button v-for="client in clients" :key="client.id" type="button" :class="['client-list-item', { active: selectedId === client.id }]" @click="selectClient(client)">
        <span :class="['status-dot', client.isEnabled ? 'ok' : 'bad']" />
        <div><strong>{{ client.name }}</strong><small>{{ platformLabel(client.platform) }} · {{ client.slug }}</small></div>
        <code>{{ client.keyPrefix }}...</code>
      </button>
      <div v-if="!clients.length && !loading" class="empty-state">暂无客户端</div>
    </aside>

    <section class="surface-section client-editor">
      <template v-if="selectedClient">
        <div class="section-header">
          <div><span class="eyebrow">CLIENT ACCESS</span><h2>{{ selectedClient.name }}</h2></div>
          <div class="header-command-group">
            <button class="button secondary" type="button" :disabled="rotating || saving" @click="rotateKey"><RotateCw :size="16" />{{ rotating ? "轮换中" : "轮换 Key" }}</button>
            <button class="button primary" type="button" :disabled="saving || rotating || !draft.name.trim() || !draft.slug.trim()" @click="saveClient"><Save :size="16" />{{ saving ? "保存中" : "保存" }}</button>
          </div>
        </div>

        <dl class="client-meta-grid">
          <div><dt>Key 前缀</dt><dd><code>{{ selectedClient.keyPrefix }}...</code></dd></div>
          <div><dt>最近使用</dt><dd>{{ formatTime(selectedClient.lastUsedAt) }}</dd></div>
          <div><dt>创建时间</dt><dd>{{ formatTime(selectedClient.createdAt) }}</dd></div>
        </dl>

        <div class="client-form-grid">
          <label class="form-field"><span>客户端名称</span><input v-model="draft.name" /></label>
          <label class="form-field"><span>客户端标识</span><input v-model="draft.slug" autocomplete="off" /><small>仅小写字母、数字和连字符。</small></label>
          <label class="form-field"><span>终端平台</span><select v-model="draft.platform"><option value="ANDROID">Android</option><option value="WINDOWS">Windows</option><option value="WEB">Web</option><option value="OTHER">其他</option></select></label>
          <label class="toggle-control client-enabled-toggle"><input v-model="draft.isEnabled" type="checkbox" /><span class="toggle-track" /><span>{{ draft.isEnabled ? "允许客户端访问" : "客户端已停用" }}</span></label>
        </div>

        <div class="access-section client-agent-access">
          <div class="section-header slim"><div><span class="eyebrow">授权</span><h3>可调用智能体</h3></div><ShieldCheck :size="18" /></div>
          <label v-for="agent in agents" :key="agent.id" class="access-row">
            <input v-model="draft.agentIds" type="checkbox" :value="agent.id" />
            <span>{{ agent.name }}</span><small>{{ agent.slug }} · {{ agent.isEnabled ? "已启用" : "已停用" }}</small>
          </label>
          <div v-if="!agents.length" class="empty-state compact-empty">请先创建智能体</div>
        </div>

        <p v-if="success" class="form-success"><Check :size="16" />{{ success }}</p>
        <p v-if="error" class="form-error">{{ error }}</p>
      </template>
      <div v-else class="client-editor-empty"><MonitorSmartphone :size="34" /><strong>尚未创建客户端</strong><button class="button primary" type="button" @click="openCreate"><Plus :size="16" />创建客户端</button></div>
    </section>

    <div v-if="createOpen" class="modal-backdrop" @click.self="createOpen = false">
      <form class="modal-panel client-create-modal" @submit.prevent="createClient">
        <div class="modal-header"><div><span class="eyebrow">NEW CLIENT</span><h2>创建客户端</h2></div><button class="icon-button" type="button" title="关闭" @click="createOpen = false"><X :size="18" /></button></div>
        <div class="form-stack">
          <label class="form-field"><span>客户端名称</span><input v-model="createDraft.name" placeholder="仓库 Android 终端" autofocus /></label>
          <label class="form-field"><span>客户端标识</span><input v-model="createDraft.slug" placeholder="warehouse-android" autocomplete="off" /><small>创建后仍可修改。</small></label>
          <label class="form-field"><span>终端平台</span><select v-model="createDraft.platform"><option value="ANDROID">Android</option><option value="WINDOWS">Windows</option><option value="WEB">Web</option><option value="OTHER">其他</option></select></label>
          <div class="form-field"><span>初始智能体授权</span><div class="create-agent-list"><label v-for="agent in agents" :key="agent.id" class="access-row"><input v-model="createDraft.agentIds" type="checkbox" :value="agent.id" /><span>{{ agent.name }}</span><small>{{ agent.slug }}</small></label><div v-if="!agents.length" class="empty-state compact-empty">暂无智能体</div></div></div>
        </div>
        <p v-if="createError" class="form-error">{{ createError }}</p>
        <div class="modal-actions"><button class="button secondary" type="button" :disabled="creating" @click="createOpen = false">取消</button><button class="button primary" type="submit" :disabled="creating || !createDraft.name.trim() || !createDraft.slug.trim()"><KeyRound :size="16" />{{ creating ? "创建中" : "创建并生成 Key" }}</button></div>
      </form>
    </div>

    <div v-if="keyOpen" class="modal-backdrop">
      <section class="modal-panel client-key-modal" role="dialog" aria-modal="true" aria-labelledby="client-key-title">
        <div class="client-key-icon"><KeyRound :size="24" /></div>
        <span class="eyebrow">CLIENT KEY</span>
        <h2 id="client-key-title">{{ revealedClientName }} 的访问 Key</h2>
        <p>该 Key 只显示一次，关闭后无法再次查看。</p>
        <div class="revealed-key"><code>{{ revealedKey }}</code><button class="icon-button" type="button" :title="copied ? '已复制' : '复制 Key'" @click="copyKey"><Check v-if="copied" :size="17" /><Copy v-else :size="17" /></button></div>
        <div class="credential-security-note"><ShieldCheck :size="18" /><div><strong>安全分发</strong><span>仅发送给对应终端，并使用系统安全存储保存。轮换后旧 Key 会立即失效。</span></div></div>
        <div class="modal-actions"><button class="button primary" type="button" @click="closeKey">我已保存，关闭</button></div>
      </section>
    </div>
  </div>
</template>
